import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';

import { CustomButton, Text } from '../../../shared/uiComponents';
import { useCheckPermission } from '../../../shared/Helpers/hooks';
import { TEXT } from '../../../shared/Helpers/colors';
import { PERMISSIONS } from '../../../App/constants';
import { AdminTypes } from '../../../components/Menu';
// import BackdateInsurance from "./backdateInsurance";

import { DispatchProperties, useSelector } from '../../../redux/store';
import { clearActiveAuthorization } from '../../../redux/State/clientSlice/insuranceAuthorizationSlice';
import { clearClients, clearTherapists } from '../../../redux/State/clientSlice/clientSlice';
import {
	AddressIcon,
	DateOfBirthIcon,
	EditIcon,
	InsuranceIcon,
	MemberIcon,
	PersonIcon,
} from '../../../shared/Assets/svg';

const ClientInfo = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();

	const ALLOCATE = useCheckPermission(PERMISSIONS.INSURANCE_AUTHORIZATION.ALLOCATE);
	const ASSIGN_BCBA = useCheckPermission(PERMISSIONS.SOAPNOTE.ASSIGN_BCBA);
	const ASSIGN_BT = useCheckPermission(PERMISSIONS.SOAPNOTE.ASSIGN_RBT);
	const EDIT = useCheckPermission(PERMISSIONS.CLIENT.EDIT);

	const { id, dateOfBirthday, address, insurances, caseCoordinator } = useSelector((state) => state.client.client);
	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;
	const isBT = role?.section.id === AdminTypes.bt;

	const isAssignAllowed = ASSIGN_BCBA.permissionGranted || ASSIGN_BT.permissionGranted;
	const canEdit = EDIT.permissionGranted && isAdmin;
	const primaryInsurance = insurances?.find((i) => i.type === 1);

	const navigateAssignTherapistHandler = () => {
		if (!id) return;
		navigate(`/clients/client/assign/${id}`);
		dispatch(clearActiveAuthorization());
		dispatch(clearTherapists());
		dispatch(clearClients());
	};

	const navigateAllocateHours = () => {
		dispatch(clearActiveAuthorization());
		navigate(`/clients/client/allocate-authorization/${id}`);
	};

	return (
		<div className="clientInfoDetailsCard">
			<div className="clientInfoDetailsRow">
				<Text title="Client details" type="h6" fontWeight="semiBold" />
				{canEdit && (
					<Link to={`/clients/${id}`} className="editClientInfoButton">
						<Tooltip title="Edit">
							<span>
								<EditIcon size={40} />
							</span>
						</Tooltip>
					</Link>
				)}
			</div>

			<div className="clientInfoDetailsRow">
				<div className="clientInfoDetailsRowIcon">
					<DateOfBirthIcon />
				</div>
				<div className="clientInfoDetailsRowText">
					<Text type="pxs" fontWeight="semiBold" title="Birthday" color={TEXT.TERTIARY} />
					<Text title={dayjs(dateOfBirthday).format('MM/DD/YYYY')} type="psm" fontWeight="semiBold" />
				</div>
			</div>

			<div className="clientInfoDetailsRow">
				<div className="clientInfoDetailsRowIcon">
					<AddressIcon />
				</div>
				<div className="clientInfoDetailsRowText">
					<Text type="pxs" fontWeight="semiBold" title="Address" color={TEXT.TERTIARY} />
					<Text title={address || '-'} type="psm" fontWeight="semiBold" />
				</div>
			</div>

			{primaryInsurance && !isBT && (
				<div className="clientInfoDetailsRow">
					<div className="clientInfoDetailsRowIcon">
						<InsuranceIcon />
					</div>
					<div className="clientInfoDetailsRowText">
						<Text type="pxs" fontWeight="semiBold" title="Primary Insurance" color={TEXT.TERTIARY} />
						<Text title={primaryInsurance.name || '-'} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			)}

			{primaryInsurance?.insuranceNumber && !isBT && (
				<div className="clientInfoDetailsRow">
					<div className="clientInfoDetailsRowIcon">
						<MemberIcon />
					</div>
					<div className="clientInfoDetailsRowText">
						<Text type="pxs" fontWeight="semiBold" title="Member ID" color={TEXT.TERTIARY} />
						<Text title={primaryInsurance.insuranceNumber || '-'} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			)}

			{caseCoordinator && (
				<div className="clientInfoDetailsRow">
					<div className="clientInfoDetailsRowIcon">
						<PersonIcon />
					</div>
					<div className="clientInfoDetailsRowText">
						<Text type="pxs" fontWeight="semiBold" title="Case Coordinator" color={TEXT.TERTIARY} />
						<Text title={caseCoordinator.fullName || '-'} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			)}

			{isAssignAllowed && (
				<CustomButton
					title="Assign Provider"
					onClick={navigateAssignTherapistHandler}
					className="marginTop16"
					disabled={!id}
				/>
			)}

			{ALLOCATE.permissionGranted && (
				<CustomButton
					title="Allocate hours"
					buttonType="secondaryGray"
					onClick={navigateAllocateHours}
					className="marginTop8 marginBottom8"
					disabled={!id}
				/>
			)}

			{/* {isAdmin && <BackdateInsurance />} */}
		</div>
	);
};

export default ClientInfo;
